// One-shot event tutorials. Small popups that fire the first time the
// player does something on the board (plays a unit, sees the cell-stack
// expand button). Reuses the TutorialOverlay + tutorialState stack the
// main board walkthrough runs on, so the Settings "reset all tutorials"
// button and the tutorials-enabled toggle apply to these keys too.
//
// Mechanism: trigger sites call queueEventTutorial(host, key). Once the
// seen-flag store is hydrated and the key qualifies, the key joins a
// FIFO queue and a collapsing timer polls until the board is quiet
// enough to show it. Blocked states re-arm rather than drop.
//
// These tutorials are MANDATORY: the overlay offers no Skip / ESC /
// Continue exit. The only way out is performing the taught action, which
// completeEventTutorialOnAction detects from state transitions (ring
// menu opened, cell fan opened) at the componentDidUpdate chokepoint,
// so every input path (shift-click, expand button, held hotkey) counts.
// A key is only marked seen once its action has actually been performed.

import { hydrateTutorialState, markTutorialSeen, shouldAutoPlay } from '../../../utils/arena/tutorialState';

export const FIRST_UNIT_TUTORIAL_KEY = 'board-first-unit-v1';
export const CELL_STACK_TUTORIAL_KEY = 'board-cell-stack-v1';

// Pure gate over a plain snapshot (see snapshotFor) so tests can
// exercise it without a GameBoard host. Any truthy field means "the
// screen is busy, hold the tutorial".
export function eventTutorialBlocked(s) {
  return !!(
    s.isLoading || s.dragging
    || s.showBoardTutorial || s.wantsBoardTutorial
    || s.showFirstPlayerDice || s.diceModalPending
    || s.showDeckPicker
    || s.ringMenu || s.focusedCell
    || s.combatScene || s.pendingServerChoice || s.pendingCellTarget
    || s.eventTutorialOpen
  );
}

function snapshotFor(host) {
  return {
    isLoading: !!host.state.isLoading,
    dragging: !!host.dragging || !!host.handDrag || !!host._unitHandDrag,
    showBoardTutorial: !!host.state.showBoardTutorial,
    wantsBoardTutorial: !!host._wantsBoardTutorial,
    showFirstPlayerDice: !!host.state.showFirstPlayerDice,
    diceModalPending: !!host._diceModalPending,
    showDeckPicker: !!host.state.showDeckPicker,
    ringMenu: !!host.state.ringMenu,
    focusedCell: !!host.state.focusedCell,
    combatScene: !!host.state.combatScene,
    pendingServerChoice: !!host.state.pendingServerChoice,
    pendingCellTarget: !!host.state.pendingCellTarget,
    eventTutorialOpen: !!host.state.eventTutorial,
  };
}

// Trigger entry point. Cheap to call per event: after the first call
// per key it is a single Set lookup. `ctx` rides along to the render
// site (the cell-stack tutorial passes { cellKey } so the spotlight
// targets the badge that triggered it).
export function queueEventTutorial(host, key, ctx) {
  if (host._unmounted || host.props.isSpectating) return;
  if (!host._eventTutorialChecked) host._eventTutorialChecked = new Set();
  if (host._eventTutorialChecked.has(key)) return;
  host._eventTutorialChecked.add(key);
  const profileId = host.props.profile?.id;
  if (!profileId) return;
  // Same hydrate-before-decide pattern as the main walkthrough
  // (boardLifecycle componentDidMount). Resolves instantly once the
  // store has hydrated.
  hydrateTutorialState().then(() => {
    if (host._unmounted) return;
    if (!shouldAutoPlay(profileId, key)) return;
    if (!host._eventTutorialQueue) host._eventTutorialQueue = [];
    host._eventTutorialQueue.push({ key, ctx: ctx || null });
    scheduleEventTutorialCheck(host, 700);
  });
}

// Collapsing timer, mirror of scheduleBoardTutorialCheck.
export function scheduleEventTutorialCheck(host, delayMs) {
  if (host._unmounted) return;
  if (host._eventTutorialTimer) clearTimeout(host._eventTutorialTimer);
  host._eventTutorialTimer = setTimeout(() => {
    host._eventTutorialTimer = null;
    maybeShowEventTutorial(host);
  }, Math.max(0, delayMs));
}

export function maybeShowEventTutorial(host) {
  if (host._unmounted) return;
  const q = host._eventTutorialQueue;
  if (!q || q.length === 0) return;
  if (eventTutorialBlocked(snapshotFor(host))) {
    scheduleEventTutorialCheck(host, 1200);
    return;
  }
  host.setState({ eventTutorial: q.shift() });
}

// Safety valve for onDismiss. Unreachable through the mandatory
// overlay's UI (no Skip, no ESC, gated Continue), but if a dismiss
// ever fires anyway, mark seen and move on rather than re-showing
// forever.
export function handleEventTutorialDismiss(host) {
  const key = host.state.eventTutorial?.key;
  const profileId = host.props.profile?.id;
  if (key && profileId) markTutorialSeen(profileId, key);
  host.setState({ eventTutorial: null });
  scheduleEventTutorialCheck(host, 800);
}

// Called from boardLifecycle.componentDidUpdate, the one chokepoint
// every state change passes through. Watching the transitions (rather
// than the individual open sites) catches every path that opens the
// ring menu or the cell fan. Also retires a tutorial still sitting in
// the queue: a player who performs the action before the popup lands
// has demonstrated the feature and should not be lectured about it.
export function completeEventTutorialOnAction(host, prevState) {
  if (!prevState) return;
  if (!prevState.ringMenu && host.state.ringMenu) {
    completeEventTutorial(host, FIRST_UNIT_TUTORIAL_KEY);
  }
  if (!prevState.focusedCell && host.state.focusedCell) {
    completeEventTutorial(host, CELL_STACK_TUTORIAL_KEY);
  }
}

function completeEventTutorial(host, key) {
  const open = host.state.eventTutorial;
  const q = host._eventTutorialQueue;
  const queuedIdx = q ? q.findIndex((entry) => entry.key === key) : -1;
  if (open?.key !== key && queuedIdx < 0) return;
  const profileId = host.props.profile?.id;
  if (profileId) markTutorialSeen(profileId, key);
  if (queuedIdx >= 0) q.splice(queuedIdx, 1);
  if (open?.key === key) {
    host.setState({ eventTutorial: null });
    scheduleEventTutorialCheck(host, 800);
  }
}
